/**
 * PiratePagesEditor — admin UI to manage the 10 per-pirate awareness pages.
 *
 * Each page (e.g. /peirateia/dikastis) uses a uniform 5-section template:
 *   1) Η Σκηνή       — client quote / scene
 *   2) Ο Πειρατής   — name + promise
 *   3) Η Στροφή     — realization
 *   4) Ο Καθρέφτης  — mirror question
 *   5) Soft CTA     — invitation + buttons (Test / Viber / Workshop / custom)
 *
 * Data model matches PiratePageContent in `backend/routes/pirate_pages.py`.
 * Mounted as an embedded tab from AdminPageMVP («📖 Σελίδες Πειρατών»).
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ExternalLink, Save, RefreshCw, Eye, Sparkles, X, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Keep in sync with backend PIRATE_SLUGS + SESSION_SLUGS
const PIRATE_LIST = [
  { slug: "dikastis",     label: "Ο Κριτής (Δικαστής)" },
  { slug: "stratigos",    label: "Ο Στρατηγός (Ελεγκτικός)" },
  { slug: "akourastos",   label: "Ο Ακούραστος" },
  { slug: "petychimenos", label: "Ο Πετυχημένος" },
  { slug: "teleios",      label: "Ο Τέλειος" },
  { slug: "anisychos",    label: "Ο Ανήσυχος" },
  { slug: "logikos",      label: "Ο Λογικός" },
  { slug: "kalos",        label: "Ο Καλός" },
  { slug: "apofeytikos",  label: "Ο Αποφευκτικός" },
  { slug: "thyma",        label: "Ο Πληγωμένος (Παραπονούμενος)" },
  // ── Πειρατές Συνεδριών (Session pirates) — δεν φαίνονται στο brand carousel
  { slug: "apodeixis",    label: "Ο Πειρατής της Απόδειξης (Συνεδριών)" },
];

const CTA_KEYS = [
  { key: "cta_test",     title: "Τεστ",         defaults: { label: "Κάνε το Τεστ", url: "/q" } },
  { key: "cta_viber",    title: "Viber",        defaults: { label: "Μπες στην ομάδα Viber", url: "" } },
  { key: "cta_workshop", title: "Εργαστήριο",   defaults: { label: "Δες το εργαστήριο", url: "/workshop-register" } },
  { key: "cta_custom",   title: "Custom (opt.)", defaults: { label: "", url: "" } },
];

const authHeaders = () => {
  const token = sessionStorage.getItem("admin-token") || "";
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

const Field = ({ label, value, onChange, textarea, rows = 3, placeholder, testid }) => (
  <label className="block mb-4">
    <span className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">{label}</span>
    {textarea ? (
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-100 outline-none text-sm text-slate-900 leading-relaxed resize-y"
      />
    ) : (
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-100 outline-none text-sm text-slate-900"
      />
    )}
  </label>
);

const Section = ({ title, num, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
    <div className="flex items-center gap-2 mb-4">
      <span className="w-7 h-7 rounded-full bg-amber-100 text-amber-700 font-bold text-sm flex items-center justify-center">{num}</span>
      <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide">{title}</h3>
    </div>
    {children}
  </div>
);

export default function PiratePagesEditor() {
  const navigate = useNavigate();
  const { slug: urlSlug } = useParams();
  const [slug, setSlug] = useState(urlSlug && PIRATE_LIST.some((p) => p.slug === urlSlug) ? urlSlug : "dikastis");
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const refreshPreview = () => setPreviewKey((k) => k + 1);

  // ── AI Auto-fill state ─────────────────────────────────────────────────
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiDescription, setAiDescription] = useState("");
  const [aiRunning, setAiRunning] = useState(false);

  const load = async (targetSlug) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/pirate-pages/${targetSlug}`);
      // Ensure CTA objects exist (backend may omit defaults)
      const safe = { ...data };
      CTA_KEYS.forEach(({ key, defaults }) => {
        if (!safe[key] || typeof safe[key] !== "object") {
          safe[key] = { enabled: key !== "cta_custom", label: defaults.label, url: defaults.url };
        }
      });
      setContent(safe);
    } catch (e) {
      toast.error("Αποτυχία φόρτωσης");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(slug); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [slug]);

  const patch = (key, value) => setContent((c) => ({ ...c, [key]: value }));
  const patchCta = (ctaKey, field, value) =>
    setContent((c) => ({ ...c, [ctaKey]: { ...(c[ctaKey] || {}), [field]: value } }));

  const save = async () => {
    if (!content) return;
    setSaving(true);
    try {
      await axios.put(`${API}/pirate-pages/${slug}`, content, authHeaders());
      toast.success("Αποθηκεύτηκε ✓");
      // Auto-refresh preview after a short delay so the new content loads
      setTimeout(refreshPreview, 400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία αποθήκευσης");
    } finally {
      setSaving(false);
    }
  };

  const previewUrl = useMemo(() => `/peirateia/${slug}`, [slug]);

  // ── AI Auto-fill handler ───────────────────────────────────────────────
  const runAiAutofill = async () => {
    const desc = aiDescription.trim();
    if (desc.length < 40) {
      toast.error("Γράψε τουλάχιστον 40 χαρακτήρες περιγραφής.");
      return;
    }
    setAiRunning(true);
    // Το LLM διαρκεί ~1-2 min. Το Cloudflare/proxy κόβει στα ~60s με 502/504
    // ΑΛΛΑ η κλήση συνεχίζει στο backend και σώζει το DB. Αν πέσει timeout, κάνουμε
    // background polling: κάθε 8 sec ελέγχουμε αν το DB doc έχει γεμίσει.
    const pollForCompletion = async (baseline) => {
      for (let i = 0; i < 20; i++) { // ~160 sec max
        await new Promise((r) => setTimeout(r, 8000));
        try {
          const { data } = await axios.get(`${API}/pirate-pages/${slug}`);
          const filled = ["recognize_body","what_does_body","thoughts_body","emotions_body","lie_body","impact_body","origin_body"]
            .filter((k) => ((data[k] || "").trim().length > (baseline[k] || 0) + 100)).length;
          if (filled >= 5) return data;
        } catch { /* keep polling */ }
      }
      return null;
    };

    // Κρατάμε αρχικά μήκη ως baseline για να ξέρουμε αν άλλαξε
    const baseline = {};
    ["recognize_body","what_does_body","thoughts_body","emotions_body","lie_body","impact_body","origin_body"]
      .forEach((k) => { baseline[k] = (content?.[k] || "").trim().length; });

    try {
      const { data } = await axios.post(
        `${API}/admin/pirate-pages/${slug}/auto-fill`,
        { description: desc },
        { ...authHeaders(), timeout: 180000 }, // 3-min client timeout
      );
      if (data?.content) {
        applyContent(data.content);
        toast.success(`✨ Auto-fill έτοιμο (${data.sections_set}/7 sections)`);
        setAiModalOpen(false);
        setAiDescription("");
        setTimeout(refreshPreview, 400);
      }
    } catch (e) {
      // Ενδέχεται να είναι Cloudflare 502/504 timeout — το backend συνεχίζει.
      toast.info("Το AI γράφει ακόμα... περιμένω το αποτέλεσμα (έως 2 λεπτά).");
      const doc = await pollForCompletion(baseline);
      if (doc) {
        applyContent(doc);
        toast.success("✨ Auto-fill ολοκληρώθηκε!");
        setAiModalOpen(false);
        setAiDescription("");
        setTimeout(refreshPreview, 400);
      } else {
        toast.error("Το auto-fill καθυστέρησε. Δοκίμασε ξανά ή έλεγξε τη σελίδα σε λίγο.");
      }
    } finally {
      setAiRunning(false);
    }
  };

  // Helper: εφαρμόζει έτοιμο pirate-page doc στο form state
  const applyContent = (raw) => {
    const safe = { ...raw };
    CTA_KEYS.forEach(({ key, defaults }) => {
      if (!safe[key] || typeof safe[key] !== "object") {
        safe[key] = { enabled: key !== "cta_custom", label: defaults.label, url: defaults.url };
      }
    });
    setContent(safe);
  };

  return (
    <div className="px-3 sm:px-4 pt-4 pb-10" data-testid="pirate-pages-editor">
      <div className="max-w-5xl mx-auto space-y-5">
        {/* Back to admin — deterministic destination, όχι navigate(-1) που
            μπορεί να γυρίσει σε άσχετη σελίδα (deep-link entry). */}
        <button
          type="button"
          onClick={() => navigate("/admin")}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50 hover:border-slate-400 transition shadow-sm"
          data-testid="pirate-editor-back-to-admin"
        >
          <ArrowLeft className="w-4 h-4" /> Πίσω στο Admin
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 flex-wrap justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-extrabold text-slate-900">📖 Σελίδες Πειρατών</h1>
            <p className="text-xs text-slate-500 mt-1">
              Επεξεργασία περιεχομένου για τις 10 σελίδες awareness (μία ανά πειρατή) · <code className="bg-slate-100 px-1.5 py-0.5 rounded">/peirateia/[slug]</code>
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={async () => {
                if (!window.confirm("Θα εφαρμοστούν τα έτοιμα κείμενα του Κωνσταντίνου στις σελίδες πειρατών (όσες υπάρχουν στον κώδικα). Συνέχεια;")) return;
                try {
                  const { data } = await axios.post(`${API}/admin/pirate-pages/apply-curated`, {}, authHeaders());
                  toast.success(`✓ Εφαρμόστηκαν κείμενα σε ${data.count} πειρατή/ές: ${data.applied.join(", ")}`);
                  load(slug);
                  setTimeout(refreshPreview, 400);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Αποτυχία εφαρμογής");
                }
              }}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold shadow-sm transition"
              data-testid="apply-curated-btn"
              title="Γράφει στη βάση τα έτοιμα κείμενα που έδωσε ο Κωνσταντίνος (τρέξε το και στο production admin μετά από deploy)"
            >
              📥 Έτοιμα κείμενα
            </button>
            <button
              type="button"
              onClick={() => setAiModalOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-fuchsia-600 to-indigo-600 hover:from-fuchsia-500 hover:to-indigo-500 text-white text-sm font-bold shadow-sm transition"
              data-testid="ai-autofill-open"
              title="Δώσε μια σύντομη περιγραφή του πειρατή και το AI θα συμπληρώσει τα 7 sections"
            >
              <Sparkles className="w-4 h-4" /> AI Auto-fill
            </button>
            <a
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700 transition"
              data-testid="preview-page-link"
            >
              <ExternalLink className="w-4 h-4" /> Προβολή σελίδας
            </a>
            <button
              type="button"
              onClick={() => {
                const link = `${window.location.origin}/api/s/${slug}`;
                navigator.clipboard.writeText(link);
                toast.success("✓ Αντιγράφηκε link κοινοποίησης με το εικονίδιο του πειρατή");
              }}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-bold shadow-sm transition"
              data-testid="copy-share-link-btn"
              title="Link για WhatsApp/Facebook/Viber — δείχνει το εικονίδιο του συγκεκριμένου πειρατή στο preview"
            >
              🔗 Link κοινοποίησης
            </button>
          </div>
        </div>

        {/* Pirate selector */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <label className="block">
            <span className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
              Επίλεξε πειρατή
            </span>
            <select
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-100 outline-none text-sm text-slate-900 font-medium bg-white"
              data-testid="pirate-slug-select"
            >
              {PIRATE_LIST.map((p) => (
                <option key={p.slug} value={p.slug}>{p.label}</option>
              ))}
            </select>
          </label>
        </div>

        {loading || !content ? (
          <div className="text-center py-10 text-slate-500">Φόρτωση…</div>
        ) : (
          <>
            {/* HERO */}
            <Section title="Hero" num="0">
              <Field label="Όνομα πειρατή (auto από πειρατές)" value={content.pirate_name} onChange={(v) => patch("pirate_name", v)} testid="field-pirate-name" />
              <Field label="Custom εικόνα URL (προαιρετικό — αν κενό, μπαίνει η επίσημη)" value={content.custom_image_url} onChange={(v) => patch("custom_image_url", v)} placeholder="/uploads/... ή https://..." testid="field-custom-image" />
            </Section>

            {/* 1. Πώς τον αναγνωρίζεις — μακροσκελής αφήγηση (πρώτο section) */}
            <Section title="Πώς τον αναγνωρίζεις — μακροσκελής αφήγηση" num="1">
              <Field label="Πλήρες κείμενο αφήγησης (χρησιμοποίησε κενές γραμμές για παραγράφους)" value={content.recognize_body} onChange={(v) => patch("recognize_body", v)} textarea rows={16} testid="field-recognize" />
            </Section>

            {/* 2. Τι κάνει */}
            <Section title="Τι κάνει" num="2">
              <Field label="Σύντομη περιγραφή" value={content.what_does_body} onChange={(v) => patch("what_does_body", v)} textarea rows={5} testid="field-what-does" />
            </Section>

            {/* 3. Οι σκέψεις του */}
            <Section title="Οι σκέψεις του" num="3">
              <Field label="Λίστα σκέψεων (μία ανά γραμμή)" value={content.thoughts_body} onChange={(v) => patch("thoughts_body", v)} textarea rows={5} placeholder="• Να τι δεν πάει καλά με εμένα..." testid="field-thoughts" />
            </Section>

            {/* 4. Τα συναισθήματα του */}
            <Section title="Τα συναισθήματα του" num="4">
              <Field label="Ποια συναισθήματα παράγει" value={content.emotions_body} onChange={(v) => patch("emotions_body", v)} textarea rows={5} testid="field-emotions" />
            </Section>

            {/* 5. Το ψέμα του */}
            <Section title="Το ψέμα του" num="5">
              <Field label="Η βασική του υπόσχεση/ψέμα (σε εισαγωγικά)" value={content.lie_body} onChange={(v) => patch("lie_body", v)} textarea rows={3} placeholder="«Χωρίς εμένα δεν θα...»" testid="field-lie" />
            </Section>

            {/* 6. Επίδραση στον εαυτό & στους άλλους */}
            <Section title="Επίδραση στον εαυτό & στους άλλους" num="6">
              <Field label="Ποια επίδραση έχει ο πειρατής στη ζωή & στις σχέσεις" value={content.impact_body} onChange={(v) => patch("impact_body", v)} textarea rows={5} testid="field-impact" />
            </Section>

            {/* 7. Πώς γεννήθηκε */}
            <Section title="Πώς γεννήθηκε" num="7">
              <Field label="Η ρίζα του πειρατή" value={content.origin_body} onChange={(v) => patch("origin_body", v)} textarea rows={7} testid="field-origin" />
            </Section>

            {/* 8. Soft CTA + KOΥΜΠΙΑ */}
            <Section title="Soft CTA — κάλεσμα + κουμπιά" num="8">
              <Field label="Τίτλος CTA" value={content.soft_cta_title} onChange={(v) => patch("soft_cta_title", v)} testid="field-cta-title" />
              <Field label="Σώμα κειμένου" value={content.soft_cta_body} onChange={(v) => patch("soft_cta_body", v)} textarea rows={4} testid="field-cta-body" />
              <div className="mt-5 pt-5 border-t border-slate-200 space-y-3">
                <p className="text-xs font-bold text-slate-700 uppercase tracking-wider">Κουμπιά CTA</p>
                {CTA_KEYS.map(({ key, title, defaults }) => {
                  const cta = content[key] || { enabled: false, label: "", url: "" };
                  return (
                    <div key={key} className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-slate-800">{title}</span>
                        <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!cta.enabled}
                            onChange={(e) => patchCta(key, "enabled", e.target.checked)}
                            className="w-4 h-4 accent-amber-600"
                            data-testid={`toggle-${key}`}
                          />
                          Ενεργό
                        </label>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-2">
                        <input
                          type="text"
                          value={cta.label || ""}
                          onChange={(e) => patchCta(key, "label", e.target.value)}
                          placeholder={`Label (${defaults.label || "π.χ. Δες περισσότερα"})`}
                          className="px-3 py-1.5 rounded-md border border-slate-300 text-sm"
                          data-testid={`label-${key}`}
                        />
                        <input
                          type="text"
                          value={cta.url || ""}
                          onChange={(e) => patchCta(key, "url", e.target.value)}
                          placeholder={key === "cta_viber" ? "(κενό = auto Viber URL)" : `URL (${defaults.url || "https://..."})`}
                          className="px-3 py-1.5 rounded-md border border-slate-300 text-sm"
                          data-testid={`url-${key}`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Section>

            {/* Sticky save bar */}
            <div className="sticky bottom-4 z-10">
              <div className="bg-white rounded-xl border-2 border-amber-500 shadow-2xl p-3 flex items-center justify-between gap-3">
                <div className="text-xs text-slate-600">
                  Επεξεργάζεσαι: <strong className="text-slate-900">{PIRATE_LIST.find((p) => p.slug === slug)?.label}</strong>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => load(slug)}
                    disabled={loading}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold transition disabled:opacity-50"
                    data-testid="btn-reload"
                  >
                    <RefreshCw className="w-4 h-4" /> Επαναφορά
                  </button>
                  <button
                    onClick={save}
                    disabled={saving}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold shadow-md transition disabled:opacity-50"
                    data-testid="btn-save"
                  >
                    <Save className="w-4 h-4" /> {saving ? "Αποθήκευση…" : "Αποθήκευση"}
                  </button>
                </div>
              </div>
            </div>

            {/* ═══════ LIVE PREVIEW ═══════ */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="pirate-page-preview">
              <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-amber-600" />
                  <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide">Προεπισκόπηση σελίδας</h3>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={refreshPreview}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition"
                    data-testid="btn-refresh-preview"
                    title="Ανανέωση προεπισκόπησης"
                  >
                    <RefreshCw className="w-3.5 h-3.5" /> Ανανέωση
                  </button>
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-900 hover:bg-slate-700 text-white text-xs font-semibold transition"
                    data-testid="btn-open-preview"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> Άνοιξε σε νέα καρτέλα
                  </a>
                </div>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Ζωντανή προεπισκόπηση της <code className="bg-slate-100 px-1.5 py-0.5 rounded">{previewUrl}</code> — ανανεώνεται αυτόματα μετά από κάθε αποθήκευση.
              </p>
              <div className="rounded-lg overflow-hidden border-2 border-slate-200 bg-slate-900">
                <iframe
                  key={previewKey}
                  src={`${previewUrl}?preview=1&_=${previewKey}`}
                  title={`Προεπισκόπηση: ${slug}`}
                  className="w-full block"
                  style={{ height: "720px", border: 0 }}
                  data-testid="preview-iframe"
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* ═══════ AI Auto-fill Modal ═══════ */}
      {aiModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8" data-testid="ai-autofill-modal">
          <button
            type="button"
            aria-label="Κλείσε"
            onClick={() => !aiRunning && setAiModalOpen(false)}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />
          <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="px-6 py-5 bg-gradient-to-r from-fuchsia-600 to-indigo-600 text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                <h3 className="text-lg font-bold">AI Auto-fill · {PIRATE_LIST.find((p) => p.slug === slug)?.label}</h3>
              </div>
              <button
                onClick={() => !aiRunning && setAiModalOpen(false)}
                disabled={aiRunning}
                className="p-1.5 rounded-md hover:bg-white/20 disabled:opacity-40"
                data-testid="ai-autofill-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-600 leading-relaxed">
                Γράψε μια <strong>σύντομη περιγραφή</strong> του πειρατή (χαρακτηριστικά, συμπεριφορές, τι υπόσχεται, τι κρύβει, πώς εμφανίζεται). Το AI θα συμπληρώσει και τα <strong>7 sections</strong> της σελίδας.
              </p>
              <textarea
                value={aiDescription}
                onChange={(e) => setAiDescription(e.target.value)}
                disabled={aiRunning}
                rows={8}
                placeholder="π.χ. Ο Στρατηγός είναι ο πειρατής που έρχεται όταν έχεις μεγαλώσει σε ασταθές περιβάλλον. Ελέγχει τα πάντα — τους ανθρώπους, το χρόνο, το σώμα. Υπόσχεται ασφάλεια μέσω του control, αλλά κρύβει την τεράστια εξάντληση και τη μοναξιά. Εμφανίζεται με σφιγμένους ώμους, με λίστες σε λίστες, με ανάγκη να ξέρει τι θα γίνει..."
                className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-fuchsia-500 focus:ring-4 focus:ring-fuchsia-100 outline-none text-sm text-slate-900 leading-relaxed resize-none"
                data-testid="ai-autofill-textarea"
              />
              <p className="text-xs text-slate-500">
                {aiDescription.length} χαρακτήρες · min 40 · Recommended: 300-800.
              </p>
              <div className="flex flex-col sm:flex-row gap-2 justify-end pt-2 border-t border-slate-100">
                <button
                  onClick={() => setAiModalOpen(false)}
                  disabled={aiRunning}
                  className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-bold disabled:opacity-40"
                  data-testid="ai-autofill-cancel"
                >
                  Ακύρωση
                </button>
                <button
                  onClick={runAiAutofill}
                  disabled={aiRunning || aiDescription.trim().length < 40}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-fuchsia-600 to-indigo-600 hover:from-fuchsia-500 hover:to-indigo-500 text-white text-sm font-bold shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="ai-autofill-generate"
                >
                  {aiRunning ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Γράφει... (10-30 sec)
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" /> Δημιούργησε τα 7 sections
                    </>
                  )}
                </button>
              </div>
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                ⚠️ Θα αντικαταστήσει το τρέχον περιεχόμενο των sections. Θα ζητηθεί επιβεβαίωση μέσω αποθήκευσης — μπορείς να επεξεργαστείς τα κείμενα πριν πατήσεις «Αποθήκευση».
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
